import React, { useState } from 'react';
import { AlertCircle, X, ChevronDown, ChevronUp } from 'lucide-react';

export interface ApiErrorMessageProps {
  error: string;
  details?: string;
  onDismiss?: () => void;
  className?: string;
  variant?: 'error' | 'warning';
}

/**
 * Standardized error message component for API errors
 */
const ApiErrorMessage: React.FC<ApiErrorMessageProps> = ({
  error,
  details,
  onDismiss,
  className = '',
  variant = 'error',
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const baseClasses = 'border-l-4 p-4 my-4 flex items-start justify-between';
  const variantClasses = {
    error: 'bg-red-50 border-red-400',
    warning: 'bg-amber-50 border-amber-400',
  };

  const iconClasses = {
    error: 'text-red-500',
    warning: 'text-amber-500',
  };

  const textClasses = {
    error: 'text-red-700',
    warning: 'text-amber-700',
  };

  return (
    <div className={`${baseClasses} ${variantClasses[variant]} ${className}`}>
      <div className="flex-1">
        <div className="flex items-center">
          <AlertCircle className={`w-5 h-5 mr-2 ${iconClasses[variant]}`} />
          <p className={textClasses[variant]}>{error}</p>
          {details && (
            <button
              onClick={() => setShowDetails(!showDetails)}
              className={`ml-2 ${iconClasses[variant]} hover:opacity-70 transition-opacity`}
              aria-label={showDetails ? 'Hide details' : 'Show details'}
            >
              {showDetails ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
          )}
        </div>
        {details && showDetails && (
          <div
            className={`mt-2 p-3 rounded ${variant === 'error' ? 'bg-red-100' : 'bg-amber-100'}`}
          >
            <p className={`text-sm ${textClasses[variant]}`}>{details}</p>
          </div>
        )}
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className={`${iconClasses[variant]} hover:opacity-70 transition-opacity ml-2`}
          aria-label="Dismiss error"
        >
          <X className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default ApiErrorMessage;
